/**
 * Default Shake Product Section JavaScript
 * Handles product variant switching, pricing, form submission, and UI interactions
 */

(function () {
  'use strict';

  /**
   * Debounce utility function
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  /**
   * Initialize section by section ID
   * @param {string} sectionId - The Shopify section ID
   * @param {string} subscriptionAppId - Subscription app ID
   */
  window.initDefaultShakeProduct = function(sectionId, subscriptionAppId) {
    const getSectionRoot = () => document.getElementById('shopify-section-' + sectionId);

    // Cache DOM query selectors
    let cachedElements = {};

    const cacheElement = (key, selector, root) => {
      if (!cachedElements[key]) {
        cachedElements[key] = root.querySelector(selector);
      }
      return cachedElements[key];
    };

    const cacheElements = (key, selector, root) => {
      if (!cachedElements[key]) {
        cachedElements[key] = root.querySelectorAll(selector);
      }
      return cachedElements[key];
    };

    // Cache for parsed product data and prefetched assets
    const productDataCache = new Map();
    const prefetchedProducts = new Set();
    const prefetchedImages = new Set();
    const imageLoadCache = new Map(); // Cache for fully loaded image promises

    /**
     * Determine the storefront root (handles language prefixes)
     * @returns {string}
     */
    const getRouteRoot = () => {
      if (window.Shopify && window.Shopify.routes && typeof window.Shopify.routes.root === 'string') {
        const root = window.Shopify.routes.root;
        return root.endsWith('/') ? root : `${root}/`;
      }
      return '/';
    };

    /**
     * Safely parse product JSON stored within a product option
     * and cache the resulting object for reuse.
     * @param {HTMLElement} optionEl - The product option wrapper
     * @returns {Object|null}
     */
    const getProductDataFromOption = (optionEl) => {
      if (!optionEl) {
        return null;
      }

      const productId = optionEl.dataset.productId || optionEl.getAttribute('data-product-id');
      if (productId && productDataCache.has(productId)) {
        return productDataCache.get(productId);
      }

      const productDataScript = optionEl.querySelector('.product-data');
      if (!productDataScript) {
        return null;
      }

      try {
        const rawJson = productDataScript.textContent.trim();
        if (!rawJson) {
          return null;
        }

        const parsed = JSON.parse(rawJson);
        if (productId) {
          productDataCache.set(productId, parsed);
        }
        return parsed;
      } catch (error) {
        console.error('Error parsing product data:', error);
        return null;
      }
    };

    /**
     * Prefetch a single image URL and return a promise that resolves when loaded.
     * Uses cache to avoid duplicate requests.
     * @param {string} src - Image source URL
     * @returns {Promise<void>}
     */
    const prefetchImageSrc = (src) => {
      if (!src) {
        return Promise.resolve();
      }

      // Return cached promise if already loading/loaded
      if (imageLoadCache.has(src)) {
        return imageLoadCache.get(src);
      }

      // Create new loading promise
      const loadPromise = new Promise((resolve) => {
        // Add prefetch hint for browser
        if (document.head && !prefetchedImages.has(src)) {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.as = 'image';
          link.href = src;
          document.head.appendChild(link);
          prefetchedImages.add(src);
        }

        // Create image and load it
        const img = new Image();
        img.decoding = 'async';

        img.onload = () => resolve();
        img.onerror = () => resolve(); // Resolve even on error to not block UI

        img.src = src;
      });

      imageLoadCache.set(src, loadPromise);
      return loadPromise;
    };

    /**
     * Remove branded words from product titles for UI display.
     * @param {string} title
     * @returns {string}
     */
    const sanitizeProductTitle = (title) => {
      if (typeof title !== 'string') {
        return title;
      }

      const cleaned = title
        .replace(/\b(310|Shake)\b/gi, '')
        .replace(/\s{2,}/g, ' ')
        .trim();

      return cleaned.length ? cleaned : title.trim();
    };

    /**
     * Determine if a string refers to a serving size.
     * @param {string} value
     * @returns {'14'|'28'|null}
     */
    const detectServingSizeFromString = (value) => {
      if (!value || typeof value !== 'string') {
        return null;
      }

      const normalized = value.toLowerCase();
      if (normalized.includes('14') && normalized.includes('serv')) {
        return '14';
      }
      if (normalized.includes('28') && normalized.includes('serv')) {
        return '28';
      }
      return null;
    };

    /**
     * Identify a variant's serving size by examining its title and options.
     * @param {Object} variant
     * @returns {'14'|'28'|null}
     */
    const identifyServingSize = (variant) => {
      if (!variant || typeof variant !== 'object') {
        return null;
      }

      const servingCountCandidates = [
        variant.servings_count,
        variant.servingsCount,
        variant?.metafields?.servings?.count,
        variant?.metafields?.servings_count
      ];

      for (const candidate of servingCountCandidates) {
        const numericValue = typeof candidate === 'string' ? parseInt(candidate, 10) : candidate;
        if (Number.isFinite(numericValue)) {
          if (numericValue === 14) {
            return '14';
          }
          if (numericValue === 28) {
            return '28';
          }
        }
      }

      const candidates = [];

      if (typeof variant.title === 'string') {
        candidates.push(variant.title.split('/')[0]);
      }
      if (typeof variant.name === 'string') {
        candidates.push(variant.name.split('/')[0]);
      }
      if (typeof variant.public_title === 'string') {
        candidates.push(variant.public_title);
      }

      if (Array.isArray(variant.options)) {
        candidates.push(...variant.options);
      } else {
        if (typeof variant.option1 === 'string') candidates.push(variant.option1);
        if (typeof variant.option2 === 'string') candidates.push(variant.option2);
        if (typeof variant.option3 === 'string') candidates.push(variant.option3);
      }

      for (const candidate of candidates) {
        const size = detectServingSizeFromString(candidate);
        if (size) {
          return size;
        }
      }

      if (typeof variant.position === 'number') {
        if (variant.position === 1) {
          return '14';
        }
        if (variant.position === 2) {
          return '28';
        }
      }

      return null;
    };

    /**
     * Resolve the selected variant based on current UI state
     * @param {HTMLFormElement} form
     * @returns {string}
     */
    const getSelectedVariantId = (form) => {
      if (!form) return '';

      const variantSwitch = form.querySelector('.variant-switch input');
      const variant14 = form.getAttribute('data-variant-14');
      const variant28 = form.getAttribute('data-variant-28');

      if (variantSwitch) {
        if (variantSwitch.checked && variant28) {
          return variant28;
        }
        if (!variantSwitch.checked && variant14) {
          return variant14;
        }
      }

      const fallback = form.getAttribute('data-default-variant');
      return fallback || '';
    };

    /**
     * Build the product path including optional variant query
     * @param {string} handle
     * @param {string} variantId
     * @returns {string}
     */
    const buildProductPath = (handle, variantId) => {
      const base = `${getRouteRoot()}products/${handle}`;
      return base; // Removed variant parameter from URL
    };

    /**
     * Keep canonical link in sync with the active product
     * @param {string} href
     */
    const updateCanonicalLink = (href) => {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) return;

      const resolved = new URL(href, window.location.origin);
      canonical.setAttribute('href', resolved.href);
    };

    /**
     * Replace browser URL with selected product + variant
     * @param {HTMLElement} root
     * @param {Object|null} productData
     */
    const updateBrowserUrl = (root, productData = null) => {
      if (!window.history || typeof window.history.replaceState !== 'function') {
        return;
      }

      const form = root.querySelector(`#add-to-cart-form-${sectionId}`);
      if (!form) {
        return;
      }

      const handleFromData = productData && productData.handle ? productData.handle : null;
      const activeHandle = handleFromData || form.getAttribute('data-active-product-handle');

      if (!activeHandle) {
        return;
      }

      const variantId = getSelectedVariantId(form);
      const path = buildProductPath(activeHandle, variantId);
      const targetUrl = new URL(path, window.location.origin);
      const currentUrl = `${window.location.pathname}${window.location.search}`;

      if (currentUrl === `${targetUrl.pathname}${targetUrl.search}`) {
        if (handleFromData) {
          form.setAttribute('data-active-product-handle', handleFromData);
        }
        return;
      }

      window.history.replaceState(window.history.state, '', `${targetUrl.pathname}${targetUrl.search}`);
      updateCanonicalLink(targetUrl.href);

      form.setAttribute('data-active-product-handle', activeHandle);
      if (productData && productData.id) {
        form.setAttribute('data-active-product-id', productData.id);
      }
    };

    /**
     * Initialize media swipers for product images with enhanced cross-device support
     */
    const initMediaSwipers = (root, wrapper) => {
      if (typeof Swiper === 'undefined') {
        return;
      }

      const thumbnailEl = cacheElement('thumbnailSwiper', '.js-swiper-thumbnail', root);
      const mainEl = cacheElement('mainSwiper', '.js-swiper-main', root);

      let thumbnailSwiper;

      if (thumbnailEl && !thumbnailEl.dataset.carouselInitialized) {
        const thumbnailConfig = {
          spaceBetween: 20,
          slidesPerView: 4,
          freeMode: false,
          watchSlidesProgress: true,
          direction: 'vertical',
          slideToClickedSlide: true,
          // Enhanced touch support
          touchRatio: 1,
          touchAngle: 45,
          grabCursor: true,
          // Smooth transitions
          speed: 400,
          effect: 'slide',
          // Accessibility
          a11y: {
            enabled: true,
            prevSlideMessage: 'Previous image',
            nextSlideMessage: 'Next image',
          },
          // Keyboard navigation
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          on: {
            init: () => {
              wrapper.classList.add('loaded');
            },
          },
        };

        thumbnailSwiper = new Swiper(thumbnailEl, thumbnailConfig);
        thumbnailEl.dataset.carouselInitialized = 'true';
      }

      if (mainEl && !mainEl.dataset.carouselInitialized) {
        const prevButton = root.querySelector('.swiper-button-prev');
        const nextButton = root.querySelector('.swiper-button-next');

        const mainConfig = {
          spaceBetween: 0,
          // Smooth navigation
          speed: 400,
          effect: 'slide',
          // Enhanced touch support for mobile
          touchRatio: 1,
          touchAngle: 45,
          grabCursor: true,
          // Loop for better UX
          loop: false,
          // Preload images
          preloadImages: false,
          lazy: {
            loadPrevNext: true,
            loadPrevNextAmount: 2,
          },
          navigation: {
            prevEl: prevButton,
            nextEl: nextButton,
            disabledClass: 'swiper-button-disabled',
          },
          // Accessibility
          a11y: {
            enabled: true,
            prevSlideMessage: 'Previous product image',
            nextSlideMessage: 'Next product image',
          },
          // Keyboard navigation
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          on: {
            init: (swiper) => {
              wrapper.classList.add('loaded');
              // Update button states on init
              if (prevButton && nextButton) {
                prevButton.disabled = swiper.isBeginning;
                nextButton.disabled = swiper.isEnd;
              }
            },
            slideChange: (swiper) => {
              // Update navigation button states
              if (prevButton && nextButton) {
                prevButton.disabled = swiper.isBeginning;
                nextButton.disabled = swiper.isEnd;
              }
            },
          },
        };

        if (thumbnailSwiper) {
          mainConfig.thumbs = {
            swiper: thumbnailSwiper,
            slideThumbActiveClass: 'swiper-slide-thumb-active',
            thumbsContainerClass: 'swiper-thumbs',
          };
        }

        const mainSwiper = new Swiper(mainEl, mainConfig);
        mainEl.dataset.carouselInitialized = 'true';

        // Bind custom left/right navigation buttons
        const buttonLeft = root.querySelector('.thumbnail-button-left');
        const buttonRight = root.querySelector('.thumbnail-button-right');

        if (buttonLeft && buttonRight) {
          // Update button states
          const updateCustomButtonStates = () => {
            buttonLeft.disabled = mainSwiper.isBeginning;
            buttonRight.disabled = mainSwiper.isEnd;

            // Update ARIA labels for accessibility
            buttonLeft.setAttribute('aria-label',
              mainSwiper.isBeginning ? 'Previous image (disabled)' : 'Previous image'
            );
            buttonRight.setAttribute('aria-label',
              mainSwiper.isEnd ? 'Next image (disabled)' : 'Next image'
            );
          };

          // Throttle button clicks to prevent rapid clicking issues
          let isNavigating = false;
          const throttledNavigate = (direction) => {
            if (isNavigating) return;
            isNavigating = true;

            if (direction === 'prev') {
              mainSwiper.slidePrev();
            } else {
              mainSwiper.slideNext();
            }

            updateCustomButtonStates();
            setTimeout(() => { isNavigating = false; }, 400);
          };

          buttonLeft.addEventListener('click', (e) => {
            e.preventDefault();
            throttledNavigate('prev');
          });

          buttonRight.addEventListener('click', (e) => {
            e.preventDefault();
            throttledNavigate('next');
          });

          // Update states on slide change
          mainSwiper.on('slideChange', updateCustomButtonStates);

          // Initial state
          updateCustomButtonStates();
        }

        // Add swipe gesture indicators for mobile
        if (window.innerWidth < 1024 && mainSwiper.slides.length > 1) {
          const addSwipeHint = () => {
            const hint = document.createElement('div');
            hint.className = 'swipe-hint';
            hint.innerHTML = '<span>← Swipe →</span>';
            mainEl.appendChild(hint);

            // Remove hint after first interaction
            const removeHint = () => {
              hint.style.opacity = '0';
              setTimeout(() => hint.remove(), 300);
              mainEl.removeEventListener('touchstart', removeHint);
              mainSwiper.off('slideChange', removeHint);
            };

            mainEl.addEventListener('touchstart', removeHint, { once: true });
            mainSwiper.on('slideChange', removeHint);

            // Auto-remove after 3 seconds
            setTimeout(removeHint, 3000);
          };

          if (mainSwiper.slides.length > 1) {
            setTimeout(addSwipeHint, 500);
          }
        }
      }
    };

    /**
     * Safely parse a price value (in cents) from dataset strings.
     * @param {string|number|null|undefined} value
     * @returns {number}
     */
    const parsePriceValue = (value) => {
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '') {
          const parsed = parseInt(trimmed, 10);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
      }

      return 0;
    };

    /**
     * Bind variant switch toggle behavior
     */
    const bindVariantSwitch = (root) => {
      const variantSwitch = cacheElement('variantSwitch', '.variant-switch input', root);
      if (!variantSwitch || variantSwitch.dataset.bound === 'true') {
        return;
      }

      const subscriptionLabel = root.querySelector('.content--selling_plan label[for="subscription-' + sectionId + '"]');

      const applySubscriptionVisibility = () => {
        if (!subscriptionLabel) {
          return;
        }

        const hideAttribute = variantSwitch.checked
          ? variantSwitch.dataset.hideSubscription28
          : variantSwitch.dataset.hideSubscription14;

        const shouldHide = hideAttribute === 'true';
        subscriptionLabel.style.display = shouldHide ? 'none' : 'block';
      };

      const updatePricing = () => {
        // Skip if this is a programmatic update (not user interaction)
        if (variantSwitch.dataset.programmaticUpdate === 'true') {
          return;
        }

        // Check if user is trying to switch to a sold-out variant
        const soldOut14 = variantSwitch.dataset.soldout14 === 'true';
        const soldOut28 = variantSwitch.dataset.soldout28 === 'true';

        // If switching to 28 servings (checked) and it's sold out, prevent it
        if (variantSwitch.checked && soldOut28) {
          variantSwitch.checked = false;
          return;
        }

        // If switching to 14 servings (unchecked) and it's sold out, prevent it
        if (!variantSwitch.checked && soldOut14) {
          variantSwitch.checked = true;
          return;
        }

        updateDisplayedPrices(root);
        applySubscriptionVisibility();
        updateBrowserUrl(root);
      };

      // Check sold out states
      const soldOut14 = variantSwitch.dataset.soldout14 === 'true';
      const soldOut28 = variantSwitch.dataset.soldout28 === 'true';

      let defaultServing = variantSwitch.getAttribute('data-default-serving');
      if (!defaultServing) {
        const form = root.querySelector(`#add-to-cart-form-${sectionId}`);
        if (form) {
          defaultServing = form.getAttribute('data-default-serving') || defaultServing;
        }
      }

      // Set initial toggle position based on availability
      if (soldOut14 && !soldOut28) {
        // 14 is sold out, force to 28
        variantSwitch.checked = true;
      } else if (soldOut28 && !soldOut14) {
        // 28 is sold out, force to 14
        variantSwitch.checked = false;
      } else if (defaultServing === '28') {
        const price28 = parsePriceValue(variantSwitch.dataset.price28);
        if (price28 > 0) {
          variantSwitch.checked = true;
        }
      } else if (defaultServing === '14') {
        variantSwitch.checked = false;
      }

      updatePricing();

      const debouncedUpdatePricing = debounce(updatePricing, 150);

      // Listen for changes with debounce
      variantSwitch.addEventListener('change', debouncedUpdatePricing);

      // Add click prevention for sold-out labels (delegate to parent)
      const variantSwitchContainer = root.querySelector('.variant-switch');
      if (variantSwitchContainer && !variantSwitchContainer.dataset.clickHandlerBound) {
        variantSwitchContainer.addEventListener('click', (e) => {
          // Check if clicked element is a label span with soldout attribute
          const clickedSpan = e.target.closest('label span');
          if (clickedSpan && clickedSpan.hasAttribute('data-soldout')) {
            e.preventDefault();
            e.stopPropagation();
          }
        }, true); // Use capture phase to catch events early
        variantSwitchContainer.dataset.clickHandlerBound = 'true';
      }

      variantSwitch.dataset.bound = 'true';
    };

    /**
     * Bind form submission for add to cart
     */
    const bindForm = (root) => {
      const form = cacheElement('form', '#add-to-cart-form-' + sectionId, root);
      if (!form || form.dataset.bound === 'true') {
        return;
      }

      const submitBtn = cacheElement('submitBtn', '#add-to-cart-' + sectionId, root);
      const btnText = submitBtn ? submitBtn.querySelector('.btn-text') : null;
      const btnLoading = submitBtn ? submitBtn.querySelector('.btn-loading') : null;
      const formError = cacheElement('formError', '.form-error', root);
      const formSuccess = cacheElement('formSuccess', '.form-success', root);

      form.addEventListener('submit', (event) => {
        event.preventDefault();

        // Show loading state
        if (submitBtn && btnText && btnLoading) {
          submitBtn.disabled = true;
          btnText.style.display = 'none';
          btnLoading.style.display = 'inline';
        }
        if (formError) formError.style.display = 'none';
        if (formSuccess) formSuccess.style.display = 'none';

        // Get the variant switch to determine which serving size is selected
        const variantSwitch = root.querySelector('.variant-switch input');
        const subscriptionRadio = root.querySelector('input[name="purchase_type_' + sectionId + '"][value="subscription"]');
        const isSubscription = subscriptionRadio ? subscriptionRadio.checked : false;
        const sellingPlanSelect = root.querySelector('#selling-plan-select-' + sectionId);
        const defaultSellingPlanInput = root.querySelector('#default-selling-plan-' + sectionId);
        const defaultSellingPlanId = defaultSellingPlanInput ? defaultSellingPlanInput.value : null;
        const selectedSellingPlanId = sellingPlanSelect ? sellingPlanSelect.value : defaultSellingPlanId;

        const items = [];

        // Get the correct variant ID based on serving size selection
        const variant14Id = form.getAttribute('data-variant-14');
        const variant28Id = form.getAttribute('data-variant-28');
        const defaultVariantId = form.getAttribute('data-default-variant');

        let selectedVariantId;

        // If variant switch exists, use it to determine selection
        if (variantSwitch) {
          selectedVariantId = variantSwitch.checked ? variant28Id : variant14Id;
        } else {
          // No variant switch, use the default variant (single available variant or first variant)
          selectedVariantId = defaultVariantId;
        }

        if (!selectedVariantId) {
          if (formError) {
            formError.textContent = 'Product variant not found. Please select a serving size.';
            formError.style.display = 'block';
          }
          // Reset button state
          if (submitBtn && btnText && btnLoading) {
            submitBtn.disabled = false;
            btnText.style.display = 'inline';
            btnLoading.style.display = 'none';
          }
          return;
        }

        const line = {
          id: parseInt(selectedVariantId, 10),
          quantity: 1,
        };

        if (isSubscription && selectedSellingPlanId) {
          line.selling_plan = selectedSellingPlanId;
        }

        items.push(line);

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        })
          .then((response) => {
            if (!response.ok) {
              return response.json().then(err => { throw err; });
            }
            return response.json();
          })
          .then((data) => {
            // Show success message
            if (formSuccess) {
              formSuccess.textContent = 'Added to cart successfully!';
              formSuccess.style.display = 'block';
            }

            // Trigger cart refresh
            if (typeof window.Shopify !== 'undefined' && window.Shopify.theme) {
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            }

            // Reset button state after success
            setTimeout(() => {
              if (submitBtn && btnText && btnLoading) {
                submitBtn.disabled = false;
                btnText.style.display = 'inline';
                btnLoading.style.display = 'none';
              }
              if (formSuccess) formSuccess.style.display = 'none';
            }, 2000);
          })
          .catch((error) => {
            // Show user-friendly error
            const errorMessage = error.description || error.message || 'Unable to add item to cart. Please try again.';
            if (formError) {
              formError.textContent = errorMessage;
              formError.style.display = 'block';
            }

            // Reset button state
            if (submitBtn && btnText && btnLoading) {
              submitBtn.disabled = false;
              btnText.style.display = 'inline';
              btnLoading.style.display = 'none';
            }
          });
      });

      form.dataset.bound = 'true';
    };

    /**
     * Bind accordion toggle behavior using event delegation with enhanced accessibility
     */
    const bindAccordions = (root) => {
      // Check if already bound to prevent duplicate listeners
      if (root.dataset.accordionsBound === 'true') return;

      const tabsContainer = root.querySelector('.content--tabs');
      if (!tabsContainer) return;

      // Helper function to close an accordion
      const closeAccordion = (button, content) => {
        if (!button || !content) return;

        button.setAttribute('aria-expanded', 'false');
        button.classList.remove('active');
        content.setAttribute('aria-hidden', 'true');
        content.hidden = true;

        // Use requestAnimationFrame for smooth removal
        requestAnimationFrame(() => {
          content.classList.remove('is-open');
        });
      };

      // Helper function to open an accordion
      const openAccordion = (button, content) => {
        if (!button || !content) return;

        button.setAttribute('aria-expanded', 'true');
        button.classList.add('active');
        content.setAttribute('aria-hidden', 'false');
        content.removeAttribute('hidden');
        content.hidden = false;

        // Use requestAnimationFrame for smooth animation
        requestAnimationFrame(() => {
          content.classList.add('is-open');

          // Smooth scroll to ensure content is visible after animation starts
          setTimeout(() => {
            const buttonRect = button.getBoundingClientRect();
            const isOutOfView = buttonRect.top < 60 || buttonRect.top > window.innerHeight - 100;

            if (isOutOfView) {
              button.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        });
      };

      // Use event delegation on the tabs container
      tabsContainer.addEventListener('click', function(event) {
        const button = event.target.closest('.js-toggle');

        // If clicked element is not or within an accordion button, ignore
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();

        const contentId = button.getAttribute('aria-controls');
        const content = document.getElementById(contentId);

        if (!content) {
          return;
        }

        const isExpanded = button.getAttribute('aria-expanded') === 'true';

        // Toggle this accordion
        if (isExpanded) {
          closeAccordion(button, content);
        } else {
          openAccordion(button, content);
        }
      });

      // Keyboard navigation for accordions
      tabsContainer.addEventListener('keydown', function(event) {
        const button = event.target.closest('.js-toggle');
        if (!button) return;

        const allButtons = Array.from(tabsContainer.querySelectorAll('.js-toggle'));
        const currentIndex = allButtons.indexOf(button);

        let targetButton = null;

        switch(event.key) {
          case 'ArrowDown':
          case 'ArrowRight':
            event.preventDefault();
            targetButton = allButtons[currentIndex + 1] || allButtons[0];
            break;

          case 'ArrowUp':
          case 'ArrowLeft':
            event.preventDefault();
            targetButton = allButtons[currentIndex - 1] || allButtons[allButtons.length - 1];
            break;

          case 'Home':
            event.preventDefault();
            targetButton = allButtons[0];
            break;

          case 'End':
            event.preventDefault();
            targetButton = allButtons[allButtons.length - 1];
            break;

          case 'Enter':
          case ' ':
            event.preventDefault();
            button.click();
            return;
        }

        if (targetButton) {
          targetButton.focus();
        }
      });

      root.dataset.accordionsBound = 'true';
    };

    /**
     * Initialize and update offer progress bar based on cart total with enhanced display
     */
    const initOfferProgressBar = (root) => {
      const offerBlock = root.querySelector('.block_type--offer');
      if (!offerBlock) return;

      const progressBar = offerBlock.querySelector('.progress_bar-active');
      const progressBarContainer = offerBlock.querySelector('.offer-progress_bar');
      const messageSuccess = offerBlock.querySelector('.message--success');
      const messageProgress = offerBlock.querySelector('.message--progress');
      const messageContainer = offerBlock.querySelector('.message');

      if (!progressBar || !messageSuccess || !messageProgress) return;

      // Set up ARIA attributes for accessibility
      if (progressBarContainer) {
        progressBarContainer.setAttribute('role', 'progressbar');
        progressBarContainer.setAttribute('aria-valuemin', '0');
        progressBarContainer.setAttribute('aria-valuemax', '100');
        progressBarContainer.setAttribute('aria-label', 'Free shipping progress');
      }

      let currentPercentage = 0;

      const updateProgressBar = (cartTotal, animate = true) => {
        // Free shipping threshold (in cents) - configurable
        const freeShippingThreshold = offerBlock.dataset.threshold || 9900; // $99.00

        // Calculate percentage
        const targetPercentage = Math.min((cartTotal / freeShippingThreshold) * 100, 100);

        // Smooth animation
        if (animate && Math.abs(targetPercentage - currentPercentage) > 0.1) {
          // Add progressing class for pulse animation
          progressBar.classList.add('progressing');

          const step = (targetPercentage - currentPercentage) / 20;
          const animateProgress = () => {
            currentPercentage += step;
            if ((step > 0 && currentPercentage >= targetPercentage) ||
                (step < 0 && currentPercentage <= targetPercentage)) {
              currentPercentage = targetPercentage;
            }

            progressBar.style.width = currentPercentage + '%';

            if (progressBarContainer) {
              progressBarContainer.setAttribute('aria-valuenow', Math.round(currentPercentage));
            }

            if (Math.abs(currentPercentage - targetPercentage) > 0.1) {
              requestAnimationFrame(animateProgress);
            } else {
              // Remove progressing class after animation completes
              setTimeout(() => {
                progressBar.classList.remove('progressing');
              }, 600);
            }
          };
          requestAnimationFrame(animateProgress);
        } else {
          currentPercentage = targetPercentage;
          progressBar.style.width = targetPercentage + '%';

          if (progressBarContainer) {
            progressBarContainer.setAttribute('aria-valuenow', Math.round(targetPercentage));
          }
        }

        // Update messages based on threshold with proper ARIA
        if (cartTotal >= freeShippingThreshold) {
          messageSuccess.setAttribute('aria-hidden', 'false');
          messageProgress.setAttribute('aria-hidden', 'true');

          if (messageContainer) {
            messageContainer.classList.add('success-state');
            messageContainer.classList.remove('progress-state');
          }

          // Add celebration effect on reaching goal
          if (targetPercentage === 100 && currentPercentage < 100) {
            progressBar.classList.add('celebration');
            setTimeout(() => progressBar.classList.remove('celebration'), 1000);
          }
        } else {
          messageSuccess.setAttribute('aria-hidden', 'true');
          messageProgress.setAttribute('aria-hidden', 'false');

          if (messageContainer) {
            messageContainer.classList.remove('success-state');
            messageContainer.classList.add('progress-state');
          }

          // Calculate remaining amount
          const remaining = (freeShippingThreshold - cartTotal) / 100;
          const remainingFormatted = remaining.toFixed(2);

          // Update progress message with remaining amount
          const originalMessage = messageProgress.getAttribute('data-original-message') || messageProgress.innerHTML;
          if (!messageProgress.getAttribute('data-original-message')) {
            messageProgress.setAttribute('data-original-message', originalMessage);
          }

          // Replace amount placeholder with formatted currency
          const currencySymbol = document.querySelector('[data-cart-symbol]')?.dataset.cartSymbol || '$';
          const updatedMessage = originalMessage.replace(/\$[\d.,]+/, currencySymbol + remainingFormatted);
          messageProgress.innerHTML = updatedMessage;
        }

        // Update aria-live region for screen readers
        if (progressBarContainer) {
          const announcement = cartTotal >= freeShippingThreshold
            ? 'Free shipping unlocked!'
            : `Add ${remaining.toFixed(2)} more for free shipping`;
          progressBarContainer.setAttribute('aria-valuenow', Math.round(targetPercentage));
          progressBarContainer.setAttribute('aria-valuetext', announcement);
        }
      };

      // Fetch current cart and update progress with retry logic
      let retryCount = 0;
      const maxRetries = 3;

      const fetchCartAndUpdate = (animate = true) => {
        fetch('/cart.js')
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then(cart => {
            retryCount = 0; // Reset retry count on success
            updateProgressBar(cart.total_price, animate);

            // Store cart total for future reference
            offerBlock.dataset.lastCartTotal = cart.total_price;
          })
          .catch(() => {
            // Retry with exponential backoff
            if (retryCount < maxRetries) {
              retryCount++;
              const delay = Math.pow(2, retryCount) * 1000;
              setTimeout(() => fetchCartAndUpdate(false), delay);
            } else {
              // Use last known cart total or default to 0
              const lastTotal = parseInt(offerBlock.dataset.lastCartTotal || '0', 10);
              updateProgressBar(lastTotal, false);
            }
          });
      };

      // Initial fetch (no animation on page load)
      fetchCartAndUpdate(false);

      // Listen for cart updates with debouncing
      const debouncedFetch = debounce(() => fetchCartAndUpdate(true), 300);

      document.addEventListener('cart:refresh', debouncedFetch);
      document.addEventListener('cart:updated', debouncedFetch);

      // Also listen for custom events from other cart interactions
      window.addEventListener('cartChanged', debouncedFetch);

      // Store reference for cleanup
      if (!root.dataset.offerListenerBound) {
        root.dataset.offerListenerBound = 'true';
      }

      // Return cleanup function
      return () => {
        document.removeEventListener('cart:refresh', debouncedFetch);
        document.removeEventListener('cart:updated', debouncedFetch);
        window.removeEventListener('cartChanged', debouncedFetch);
      };
    };


    /**
     * Bind multi-product selector
     */
    const bindMultiProductSelector = (root) => {
      const form = root.querySelector(`#add-to-cart-form-${sectionId}`);
      if (!form || form.getAttribute('data-product-list-mode') !== 'true') {
        return;
      }

      const productRadios = root.querySelectorAll('.multi-product-selector .product-option input[type="radio"]');

      productRadios.forEach(radio => {
        radio.addEventListener('change', async (e) => {
          const selectedOption = e.target.closest('.product-option');
          const productData = getProductDataFromOption(selectedOption);

          if (!productData) {
            return;
          }

          // Update active state for product options
          root.querySelectorAll('.multi-product-selector .product-option').forEach(option => {
            option.classList.remove('active');
          });
          selectedOption.classList.add('active');

          // Update form with new product data (from cached JSON)
          updateFormWithProduct(root, productData);

          // Update product title and images immediately (from cached JSON)
          updateProductTitle(root, productData.title);
          updateProductImages(root, productData.images);

          // Update discount badge based on product data
          updateDiscountBadge(root, productData);

          // Fetch full product data for description and other details
          if (productData.handle) {
            try {
              const response = await fetch(`/products/${productData.handle}.js`);
              if (response.ok) {
                const fullProductData = await response.json();

                // Update description with full product data
                updateProductDescription(root, fullProductData.description);

                // Update form with complete variant data (includes all metafields)
                if (fullProductData.variants && fullProductData.variants.length > 0) {
                  const pricingMap = new Map();
                  if (Array.isArray(productData.variants)) {
                    productData.variants.forEach((variant) => {
                      pricingMap.set(String(variant.id), variant);
                    });
                  }

                  const enrichedVariants = fullProductData.variants.map((variant) => {
                    const pricing = pricingMap.get(String(variant.id)) || {};

                    const basePrice = Number.isFinite(pricing.price)
                      ? pricing.price
                      : variant.price;

                    const salePrice = Number.isFinite(pricing.sale_price)
                      ? pricing.sale_price
                      : Number.isFinite(pricing.price)
                        ? pricing.price
                        : variant.price;

                    const comparePrice = Number.isFinite(pricing.compare_price)
                      ? pricing.compare_price
                      : Number.isFinite(variant.compare_at_price)
                        ? variant.compare_at_price
                        : 0;

                    let subscriptionPrice = Number.isFinite(pricing.subscription_price)
                      ? pricing.subscription_price
                      : null;

                    if (subscriptionPrice === null) {
                      const allocationPrice = Array.isArray(variant.selling_plan_allocations) && variant.selling_plan_allocations.length > 0
                        ? variant.selling_plan_allocations[0].price
                        : null;
                      subscriptionPrice = Number.isFinite(allocationPrice) ? allocationPrice : salePrice;
                    }

                    let subscriptionCompare = Number.isFinite(pricing.subscription_compare_price)
                      ? pricing.subscription_compare_price
                      : 0;

                    if (!subscriptionCompare) {
                      if (subscriptionPrice < basePrice) {
                        subscriptionCompare = basePrice;
                      } else if (comparePrice > subscriptionPrice) {
                        subscriptionCompare = comparePrice;
                      }
                    }

                    return {
                      ...variant,
                      sale_price: salePrice,
                      compare_price: comparePrice,
                      subscription_price: subscriptionPrice,
                      subscription_compare_price: subscriptionCompare,
                      hide_subscription: pricing.hide_subscription ?? variant.hide_subscription ?? false,
                      servings_count: pricing.servings_count ?? variant.servings_count,
                    };
                  });

                  const updatedProductData = {
                    ...productData,
                    variants: enrichedVariants,
                    description: fullProductData.description,
                    selling_plan_groups: fullProductData.selling_plan_groups || [],
                    has_subscription: hasActiveSellingPlans(fullProductData.selling_plan_groups || [])
                  };
                  updateFormWithProduct(root, updatedProductData);
                }
              }
            } catch (error) {
              console.error('Error fetching full product data:', error);
              // Continue with cached data if fetch fails
            }
          }
        });
      });
    };

    /**
     * Preload ALL image assets for every product option so switching is instant.
     * @param {HTMLElement} root - Section root element
     * @returns {Promise<void>}
     */
    const prefetchProductAssets = async (root) => {
      const form = root.querySelector(`#add-to-cart-form-${sectionId}`);
      if (!form || form.getAttribute('data-product-list-mode') !== 'true') {
        return;
      }

      const productOptions = root.querySelectorAll('.multi-product-selector .product-option');
      const highPriorityPromises = []; // First images only
      const lowPriorityPromises = []; // Remaining images

      productOptions.forEach(optionEl => {
        const productId = optionEl.dataset.productId || optionEl.getAttribute('data-product-id');
        if (productId && prefetchedProducts.has(productId)) {
          return;
        }

        const productData = getProductDataFromOption(optionEl);
        if (!productData) {
          return;
        }

        if (productId) {
          prefetchedProducts.add(productId);
        }

        // Preload ALL images with priority-based loading
        if (Array.isArray(productData.images)) {
          productData.images.forEach((image, index) => {
            // Get base URL and version parameter
            const baseSrc = image.src ? image.src.split('?')[0] : '';
            const baseThumb = image.thumb ? image.thumb.split('?')[0] : '';
            const urlParams = new URLSearchParams((image.src || '').split('?')[1] || '');
            const vParam = urlParams.get('v') ? `&v=${urlParams.get('v')}` : '';

            const targetArray = index === 0 ? highPriorityPromises : lowPriorityPromises;

            if (baseSrc) {
              // Preload main image variants (prioritize 1000w for immediate display)
              targetArray.push(prefetchImageSrc(`${baseSrc}?width=1000${vParam}`));
              targetArray.push(prefetchImageSrc(`${baseSrc}?width=600${vParam}`));
              targetArray.push(prefetchImageSrc(`${baseSrc}?width=1400${vParam}`));
            }

            if (baseThumb) {
              // Preload thumbnail variants
              targetArray.push(prefetchImageSrc(`${baseThumb}?width=150${vParam}`));
              targetArray.push(prefetchImageSrc(`${baseThumb}?width=300${vParam}`));
            }
          });
        }
      });

      // Load high priority images first (first image of each product)
      await Promise.race([
        Promise.all(highPriorityPromises),
        new Promise(resolve => setTimeout(resolve, 2000)) // 2 second timeout for critical images
      ]);

      // Load remaining images in background (non-blocking)
      Promise.all(lowPriorityPromises).catch(() => {
        // Silently fail - these are nice-to-have
      });
    };

    /**
     * Update form with selected product data
     */
    const updateFormWithProduct = (root, productData) => {
      const form = root.querySelector(`#add-to-cart-form-${sectionId}`);
      if (!form) return;

      // Guard against missing variants array
      if (!productData.variants || !Array.isArray(productData.variants)) {
        console.warn('Product data missing variants array', productData);
        return;
      }

      // COMPLETELY REWRITTEN APPROACH - Simpler and more explicit
      // Step 1: Find ALL 14 and 28 serving variants (both available and sold out)
      const all14Variants = [];
      const all28Variants = [];
      const otherVariants = [];

      productData.variants.forEach((variant) => {
        const servingSize = identifyServingSize(variant);

        if (servingSize === '14') {
          all14Variants.push(variant);
        } else if (servingSize === '28') {
          all28Variants.push(variant);
        } else {
          otherVariants.push(variant);
        }
      });

      // Step 2: Find first AVAILABLE variant for each size
      const variant14Available = all14Variants.find(v => v.available === true);
      const variant28Available = all28Variants.find(v => v.available === true);

      // Step 3: Determine if each size exists in the product (regardless of availability)
      const has14ServingVariants = all14Variants.length > 0;
      const has28ServingVariants = all28Variants.length > 0;

      // Step 4: Determine sold out status
      // CRITICAL: A serving size is sold out ONLY if:
      // - The product HAS that serving size (variants exist for it)
      // - AND there's NO available variant for that size (all sold out)
      //
      // Examples:
      // - Product has 14 & 28, both available → variant14SoldOut=false, variant28SoldOut=false
      // - Product has 14 & 28, 14 sold out → variant14SoldOut=true, variant28SoldOut=false
      // - Product has only 14, available → variant14SoldOut=false, variant28SoldOut=false (no 28 variants exist)
      // - Product has only 14, sold out → variant14SoldOut=true, variant28SoldOut=false
      const variant14SoldOut = has14ServingVariants && !variant14Available;
      const variant28SoldOut = has28ServingVariants && !variant28Available;

      // Step 5: Set the variant to use (null if sold out)
      let variant14 = variant14Available || null;
      let variant28 = variant28Available || null;

      // Update form data attributes
      form.setAttribute('data-variant-14', variant14 ? variant14.id : '');
      form.setAttribute('data-variant-28', variant28 ? variant28.id : '');
      form.setAttribute('data-default-variant', variant14 ? variant14.id : (variant28 ? variant28.id : productData.variants[0]?.id || ''));
      if (productData.handle) {
        form.setAttribute('data-active-product-handle', productData.handle);
      }
      if (productData.id) {
        form.setAttribute('data-active-product-id', productData.id);
      }

      // Update variant switch UI based on available variants
      const defaultServing = productData.default_serving || '14';
      form.setAttribute('data-default-serving', defaultServing);

      updateVariantSwitchUI(root, variant14, variant28, defaultServing, variant14SoldOut, variant28SoldOut, has14ServingVariants, has28ServingVariants);

      const resolvePriceValue = (variant, key, fallback) => {
        if (!variant) return fallback;
        const candidate = variant[key];
        if (candidate !== undefined && candidate !== null) {
          const parsed = parsePriceValue(candidate);
          if (parsed > 0) {
            return parsed;
          }
        }
        return fallback;
      };

      const selectDefaultVariant = () => {
        if (defaultServing === '28' && variant28) {
          return variant28;
        }
        if (variant14) {
          return variant14;
        }
        if (variant28) {
          return variant28;
        }
        return productData.variants && productData.variants.length > 0
          ? productData.variants[0]
          : null;
      };

      const defaultVariant = selectDefaultVariant();
      if (defaultVariant) {
        const basePrice = resolvePriceValue(defaultVariant, 'sale_price', resolvePriceValue(defaultVariant, 'price', 0));
        const comparePrice = resolvePriceValue(defaultVariant, 'compare_price', resolvePriceValue(defaultVariant, 'compare_at_price', 0));
        const subscriptionPrice = resolvePriceValue(
          defaultVariant,
          'subscription_price',
          basePrice
        );
        const subscriptionCompare = resolvePriceValue(
          defaultVariant,
          'subscription_compare_price',
          subscriptionPrice < basePrice ? basePrice : comparePrice
        );

        form.setAttribute('data-default-price', `${basePrice}`);
        form.setAttribute('data-default-compare-price', `${comparePrice > 0 ? comparePrice : 0}`);
        form.setAttribute('data-default-subscription-price', `${subscriptionPrice}`);
        form.setAttribute(
          'data-default-subscription-compare-price',
          `${subscriptionCompare > 0 ? subscriptionCompare : subscriptionPrice}`
        );
      }

      // Clear the cached variant switch element to allow rebinding
      const variantSwitch = root.querySelector('.variant-switch input');
      if (variantSwitch) {
        delete variantSwitch.dataset.bound;
        delete cachedElements.variantSwitch;
      }

      // Rebind variant switch with new product data
      bindVariantSwitch(root);

      // Update selling plans
      updateSellingPlans(root, productData.selling_plan_groups);

      const hasSubscription = determineSubscriptionAvailability(productData);
      productData.has_subscription = hasSubscription;
      form.setAttribute('data-has-subscription', hasSubscription ? 'true' : 'false');
      toggleSubscriptionAvailability(root, hasSubscription);

      updateBrowserUrl(root, productData);

      // CRITICAL: Update displayed prices after form data is set
      updateDisplayedPrices(root);
    };

    /**
     * Helper to toggle element visibility without affecting layout
     */
    const setElementVisibility = (element, visible) => {
      if (!element) return;
      if (visible) {
        element.style.visibility = 'visible';
        element.style.position = 'relative';
        element.style.opacity = '1';
        element.style.pointerEvents = 'auto';
      } else {
        element.style.position = 'absolute';
        element.style.visibility = 'hidden';
        element.style.opacity = '0';
        element.style.pointerEvents = 'none';
      }
    };

    const hasActiveSellingPlans = (sellingPlanGroups) => {
      if (!Array.isArray(sellingPlanGroups)) {
        return false;
      }

      const normalizedAppId = subscriptionAppId ? String(subscriptionAppId) : null;

      return sellingPlanGroups.some((group) => {
        if (!group) return false;
        if (normalizedAppId && group.app_id) {
          if (String(group.app_id) !== normalizedAppId) {
            return false;
          }
        }
        return Array.isArray(group.selling_plans) && group.selling_plans.length > 0;
      });
    };

    const determineSubscriptionAvailability = (productData) => {
      if (!productData) return false;
      if (typeof productData.has_subscription === 'boolean') {
        return productData.has_subscription;
      }
      return hasActiveSellingPlans(productData.selling_plan_groups);
    };

    const toggleSubscriptionAvailability = (root, hasSubscription) => {
      const subscriptionLabel = root.querySelector(`label[for="subscription-${sectionId}"]`);
      const subscriptionPanel = subscriptionLabel ? subscriptionLabel.closest('.content--selling_plan') : null;
      const subscriptionRadio = root.querySelector(`input[name="purchase_type_${sectionId}"][value="subscription"]`);
      const onetimeRadio = root.querySelector(`input[name="purchase_type_${sectionId}"][value="onetime"]`);
      const subscriptionOptions = root.querySelector(`#selling-plan-container-${sectionId}`);

      if (!subscriptionPanel || !subscriptionRadio || !onetimeRadio) {
        return;
      }

      if (hasSubscription) {
        subscriptionPanel.style.display = '';
        subscriptionRadio.disabled = false;

        const wasForced = subscriptionRadio.dataset.subscriptionForcedOff === 'true';
        subscriptionRadio.dataset.subscriptionForcedOff = 'false';

        if (wasForced) {
          subscriptionRadio.checked = true;
          onetimeRadio.checked = false;
        }

        if (subscriptionOptions) {
          subscriptionOptions.style.display = subscriptionRadio.checked ? 'block' : 'none';
        }
      } else {
        subscriptionPanel.style.display = 'none';
        subscriptionRadio.checked = false;
        subscriptionRadio.disabled = true;
        subscriptionRadio.dataset.subscriptionForcedOff = 'true';
        onetimeRadio.checked = true;
        if (subscriptionOptions) {
          subscriptionOptions.style.display = 'none';
        }
      }
    };

    /**
     * Update variant switch UI based on available variants
     */
    const updateVariantSwitchUI = (root, variant14, variant28, defaultServing = '14', variant14SoldOut = false, variant28SoldOut = false, has14ServingVariants = false, has28ServingVariants = false) => {
      const variantSwitch = root.querySelector(`#variant-switch-${sectionId}`);
      const variantSwitchContainer = root.querySelector('.variant-switch');

      // Clean up any duplicate static containers (keep only first one)
      const allStaticContainers = root.querySelectorAll('.variant-switch-static, .variant-single');
      let variantSingleContainer = null;
      allStaticContainers.forEach((container, index) => {
        if (index === 0) {
          variantSingleContainer = container;
        } else {
          // Remove duplicates
          container.remove();
        }
      });

      // Only target the servings heading, not the flavor heading
      const variantHeading = root.querySelector('.section--loading > .variant-heading');

      // Both variants available - show toggle switch
      // OR if both serving sizes exist but one is sold out - show toggle with disabled state
      if ((variant14 && variant28) || (has14ServingVariants && has28ServingVariants)) {

        if (variantSwitch) {
          // Handle prices for variant14 (if available)
          if (variant14) {
            const price14 = parsePriceValue(variant14.sale_price ?? variant14.price);
            const compare14 = parsePriceValue(variant14.compare_price ?? 0);
            const subPrice14 = parsePriceValue(variant14.subscription_price ?? variant14.sale_price ?? variant14.price);
            const subCompare14 = parsePriceValue(variant14.subscription_compare_price ?? variant14.sale_price ?? variant14.price);

            variantSwitch.setAttribute('data-price14', `${price14}`);
            variantSwitch.setAttribute('data-compare-price14', `${compare14}`);
            variantSwitch.setAttribute('data-subscription-price14', `${subPrice14}`);
            variantSwitch.setAttribute('data-subscription-compare-price14', `${subCompare14}`);

            const hide14Subscription = !!(variant14.hide_subscription ?? variant14.metafields?.custom?.hide_subscription ?? false);
            variantSwitch.setAttribute('data-hide-subscription14', hide14Subscription.toString());
          } else {
            // Variant 14 is sold out - set zero prices
            variantSwitch.setAttribute('data-price14', '0');
            variantSwitch.setAttribute('data-compare-price14', '0');
            variantSwitch.setAttribute('data-subscription-price14', '0');
            variantSwitch.setAttribute('data-subscription-compare-price14', '0');
            variantSwitch.setAttribute('data-hide-subscription14', 'false');
          }

          // Handle prices for variant28 (if available)
          if (variant28) {
            const price28 = parsePriceValue(variant28.sale_price ?? variant28.price);
            const compare28 = parsePriceValue(variant28.compare_price ?? 0);
            const subPrice28 = parsePriceValue(variant28.subscription_price ?? variant28.sale_price ?? variant28.price);
            const subCompare28 = parsePriceValue(variant28.subscription_compare_price ?? variant28.sale_price ?? variant28.price);

            variantSwitch.setAttribute('data-price28', `${price28}`);
            variantSwitch.setAttribute('data-compare-price28', `${compare28}`);
            variantSwitch.setAttribute('data-subscription-price28', `${subPrice28}`);
            variantSwitch.setAttribute('data-subscription-compare-price28', `${subCompare28}`);

            const hide28Subscription = !!(variant28.hide_subscription ?? variant28.metafields?.custom?.hide_subscription ?? false);
            variantSwitch.setAttribute('data-hide-subscription28', hide28Subscription.toString());
          } else {
            // Variant 28 is sold out - set zero prices
            variantSwitch.setAttribute('data-price28', '0');
            variantSwitch.setAttribute('data-compare-price28', '0');
            variantSwitch.setAttribute('data-subscription-price28', '0');
            variantSwitch.setAttribute('data-subscription-compare-price28', '0');
            variantSwitch.setAttribute('data-hide-subscription28', 'false');
          }

          // Set sold out states for toggle switch buttons
          variantSwitch.setAttribute('data-soldout14', variant14SoldOut.toString());
          variantSwitch.setAttribute('data-soldout28', variant28SoldOut.toString());

          // Default to available serving if current default is sold out
          let activeServing = defaultServing;
          if (defaultServing === '14' && variant14SoldOut && variant28) {
            activeServing = '28';
          } else if (defaultServing === '28' && variant28SoldOut && variant14) {
            activeServing = '14';
          }
          variantSwitch.setAttribute('data-default-serving', activeServing);

          // Force toggle to available option if one is sold out
          // Only set if different from current to avoid triggering change events unnecessarily
          const targetChecked = variant14SoldOut && variant28 ? true :
                               variant28SoldOut && variant14 ? false :
                               (activeServing === '28');

          // Use a flag to prevent change event during programmatic update
          variantSwitch.dataset.programmaticUpdate = 'true';
          if (variantSwitch.checked !== targetChecked) {
            variantSwitch.checked = targetChecked;
          }
          // Remove flag after a short delay to allow the update to complete
          setTimeout(() => {
            delete variantSwitch.dataset.programmaticUpdate;
          }, 10);
        }

        // Show switch elements, hide single variant label
        setElementVisibility(variantSwitchContainer, true);
        setElementVisibility(variantHeading, true);
        setElementVisibility(variantSingleContainer, false);

        // Apply sold-out styling to toggle switch labels
        const toggleLabels = variantSwitchContainer.querySelectorAll('label span');
        if (toggleLabels.length >= 2) {
          const label14 = toggleLabels[0]; // First span is 14 servings
          const label28 = toggleLabels[1]; // Second span is 28 servings

          // Remove any existing soldout classes and data attributes
          label14.classList.remove('soldout');
          label28.classList.remove('soldout');
          label14.removeAttribute('data-soldout');
          label28.removeAttribute('data-soldout');

          // Add soldout class and data attribute to sold out options
          if (variant14SoldOut) {
            label14.classList.add('soldout');
            label14.setAttribute('data-soldout', 'true');
          }
          if (variant28SoldOut) {
            label28.classList.add('soldout');
            label28.setAttribute('data-soldout', 'true');
          }

          // Add class to container if any soldout
          if (variant14SoldOut || variant28SoldOut) {
            variantSwitchContainer.classList.add('has-soldout');
          } else {
            variantSwitchContainer.classList.remove('has-soldout');
          }
        }

        // Hide selling plan if both are sold out, otherwise show purchase options
        const allSoldOut = variant14SoldOut && variant28SoldOut;
        const sellingPlanOptions = root.querySelectorAll('.content--selling_plan');
        const otherPurchaseOptions = root.querySelectorAll('.content--add_to_cart, .ig-buttons');

        sellingPlanOptions.forEach(element => {
          setElementVisibility(element, !allSoldOut);
        });
        otherPurchaseOptions.forEach(element => {
          setElementVisibility(element, !allSoldOut);
        });

        // Update displayed prices
        updateDisplayedPrices(root);

      // Only one variant available OR sold out variants exist - show static label(s)
      } else if (variant14 || variant28 || variant14SoldOut || variant28SoldOut) {
        const has14 = !!(variant14 || variant14SoldOut);
        const has28 = !!(variant28 || variant28SoldOut);
        const both = has14 && has28;

        // Hide switch container but keep heading visible
        setElementVisibility(variantSwitchContainer, false);
        setElementVisibility(variantHeading, true);

        // Sold out flags are already set correctly from variant detection above
        // soldOut14 means: product has 14 serving size AND it's sold out
        // soldOut28 means: product has 28 serving size AND it's sold out
        const soldOut14 = variant14SoldOut;
        const soldOut28 = variant28SoldOut;
        const allSoldOut = soldOut14 && soldOut28 && has14 && has28;

        // Hide purchase options (Subscribe & Save, One-Time Purchase, Add to Cart) when all sold out
        const purchaseOptions = root.querySelectorAll('.content--selling_plan, .content--add_to_cart, .ig-buttons');
        purchaseOptions.forEach(element => {
          if (allSoldOut) {
            setElementVisibility(element, false);
          } else {
            setElementVisibility(element, true);
          }
        });

        let staticSwitchHTML = '';

        if (both) {
          // Both 14 and 28 servings exist - show both with toggle-like structure
          const badge14 = soldOut14 ? ' <span class="sold-out-badge">SOLD OUT</span>' : '';
          const badge28 = soldOut28 ? ' <span class="sold-out-badge">SOLD OUT</span>' : '';

          staticSwitchHTML = `
            <label>
              <span>14 servings${badge14}</span>
              <span>28 servings${badge28}</span>
            </label>
          `;
        } else {
          // Only one serving size exists - show as single button-like element
          const servingSize = has14 ? '14' : '28';
          const isSoldOut = (servingSize === '14' && soldOut14) || (servingSize === '28' && soldOut28);
          const soldOutBadge = isSoldOut ? ' <span class="sold-out-badge">SOLD OUT</span>' : '';

          staticSwitchHTML = `
            <label>
              <span>${servingSize} servings${soldOutBadge}</span>
            </label>
          `;
        }

        // Show or create static variant switch in same position
        if (!variantSingleContainer) {
          variantSingleContainer = document.createElement('div');
          variantSingleContainer.className = 'variant-switch-static';

          // Insert in the same position where switch would be
          if (variantSwitchContainer && variantSwitchContainer.parentNode) {
            variantSwitchContainer.parentNode.insertBefore(variantSingleContainer, variantSwitchContainer);
          }
        } else {
          // Update class to variant-switch-static and show it
          variantSingleContainer.className = 'variant-switch-static';
          setElementVisibility(variantSingleContainer, true);
        }

        // Always update the HTML content (force re-render)
        variantSingleContainer.innerHTML = staticSwitchHTML;

        const availableVariant = variant14 || variant28;

        // Only update prices if there's an available variant
        if (availableVariant && variantSwitch) {
          const servingSize = variant14 ? '14' : '28';
          const price = parsePriceValue(availableVariant.sale_price ?? availableVariant.price);
          const compare = parsePriceValue(availableVariant.compare_price ?? 0);
          const subscriptionPrice = parsePriceValue(availableVariant.subscription_price ?? availableVariant.sale_price ?? availableVariant.price);
          const subscriptionCompare = parsePriceValue(availableVariant.subscription_compare_price ?? availableVariant.sale_price ?? availableVariant.price);

          variantSwitch.setAttribute('data-price14', `${price}`);
          variantSwitch.setAttribute('data-compare-price14', `${compare}`);
          variantSwitch.setAttribute('data-subscription-price14', `${subscriptionPrice}`);
          variantSwitch.setAttribute('data-subscription-compare-price14', `${subscriptionCompare}`);
          variantSwitch.setAttribute('data-price28', `${price}`);
          variantSwitch.setAttribute('data-compare-price28', `${compare}`);
          variantSwitch.setAttribute('data-subscription-price28', `${subscriptionPrice}`);
          variantSwitch.setAttribute('data-subscription-compare-price28', `${subscriptionCompare}`);
          const hideSubscription = !!(availableVariant.hide_subscription ?? availableVariant.metafields?.custom?.hide_subscription ?? false);
          variantSwitch.setAttribute('data-hide-subscription14', hideSubscription.toString());
          variantSwitch.setAttribute('data-hide-subscription28', hideSubscription.toString());
          variantSwitch.setAttribute('data-default-serving', servingSize);

          updateDisplayedPrices(root);
        }

      // No variants available
      } else {
        if (variantSwitchContainer) {
          variantSwitchContainer.style.position = 'absolute';
          variantSwitchContainer.style.visibility = 'hidden';
          variantSwitchContainer.style.opacity = '0';
        }
        if (variantSingleContainer) {
          variantSingleContainer.style.position = 'absolute';
          variantSingleContainer.style.visibility = 'hidden';
          variantSingleContainer.style.opacity = '0';
        }
        if (variantHeading) {
          variantHeading.style.position = 'absolute';
          variantHeading.style.visibility = 'hidden';
          variantHeading.style.opacity = '0';
        }

        if (variantSwitch) {
          variantSwitch.setAttribute('data-price14', '0');
          variantSwitch.setAttribute('data-compare-price14', '0');
          variantSwitch.setAttribute('data-subscription-price14', '0');
          variantSwitch.setAttribute('data-subscription-compare-price14', '0');
          variantSwitch.setAttribute('data-price28', '0');
          variantSwitch.setAttribute('data-compare-price28', '0');
          variantSwitch.setAttribute('data-subscription-price28', '0');
          variantSwitch.setAttribute('data-subscription-compare-price28', '0');
          variantSwitch.setAttribute('data-default-serving', defaultServing);
        }

        updateDisplayedPrices(root);
      }
    };

    /**
     * Update displayed prices
     */
    const updateDisplayedPrices = (root) => {
      const form = root.querySelector(`#add-to-cart-form-${sectionId}`);
      const variantSwitch = root.querySelector(`#variant-switch-${sectionId}`);

      let price, comparePrice, subscriptionPrice, subscriptionComparePrice;

      // Check if this is a single-variant product (no variant switch)
      if (!variantSwitch) {
        // For single-variant products, read directly from form data attributes
        price = form ? parsePriceValue(form.dataset.defaultPrice) : 0;
        comparePrice = form ? parsePriceValue(form.dataset.defaultComparePrice) : 0;
        subscriptionPrice = form ? parsePriceValue(form.dataset.defaultSubscriptionPrice) : price;
        subscriptionComparePrice = form ? parsePriceValue(form.dataset.defaultSubscriptionComparePrice) : subscriptionPrice;
      } else {
        // Multi-variant product with serving switch
        const servingKey = variantSwitch.checked ? '28' : '14';

        price = parsePriceValue(variantSwitch.dataset[`price${servingKey}`]);
        comparePrice = parsePriceValue(variantSwitch.dataset[`comparePrice${servingKey}`]);
        subscriptionPrice = parsePriceValue(variantSwitch.dataset[`subscriptionPrice${servingKey}`]);
        subscriptionComparePrice = parsePriceValue(variantSwitch.dataset[`subscriptionComparePrice${servingKey}`]);
      }

      const fallbackPrice = form ? parsePriceValue(form.dataset.defaultPrice) : 0;
      const fallbackCompare = form ? parsePriceValue(form.dataset.defaultComparePrice) : 0;
      const fallbackSubscriptionPrice = form ? parsePriceValue(form.dataset.defaultSubscriptionPrice) : fallbackPrice;
      const fallbackSubscriptionCompare = form ? parsePriceValue(form.dataset.defaultSubscriptionComparePrice) : fallbackSubscriptionPrice;

      const currencyCode =
        (variantSwitch && variantSwitch.dataset.currencyCode) ||
        (window.Shopify && Shopify.currency && Shopify.currency.active) ||
        'USD';
      const currencySymbol = variantSwitch ? variantSwitch.getAttribute('data-cart-symbol') || '' : '';

      let resolvedPrice = price > 0 ? price : fallbackPrice;
      let resolvedCompare = comparePrice > 0 ? comparePrice : fallbackCompare;
      if (resolvedCompare <= 0 && resolvedPrice > 0) {
        resolvedCompare = 0;
      }

      subscriptionPrice = subscriptionPrice > 0 ? subscriptionPrice : fallbackSubscriptionPrice;
      subscriptionComparePrice = subscriptionComparePrice > 0 ? subscriptionComparePrice : fallbackSubscriptionCompare;
      if (subscriptionComparePrice <= 0 && subscriptionPrice < resolvedPrice) {
        subscriptionComparePrice = resolvedPrice;
      }

      const subscriptionPriceEl = root.querySelector(`#subscription-price-${sectionId}`);
      const noSubscriptionPriceEl = root.querySelector(`#no-subscription-price-${sectionId}`);
      const subscriptionCompareEl = root.querySelector(`#subscription-compare-price-${sectionId}`);
      const noSubscriptionCompareEl = root.querySelector(`#no-subscription-compare-price-${sectionId}`);

      setPriceDisplay(subscriptionPriceEl, subscriptionCompareEl, subscriptionPrice, subscriptionComparePrice, currencyCode, currencySymbol);
      setPriceDisplay(noSubscriptionPriceEl, noSubscriptionCompareEl, resolvedPrice, resolvedCompare, currencyCode, currencySymbol);
    };

    /**
     * Format money helper
     */
    function formatMoney(cents, currencyCode = 'USD', currencySymbol = '') {
      const amount = Number(cents);
      if (!Number.isFinite(amount)) {
        return '';
      }

      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        try {
          const moneyFormat = Shopify.money_format || Shopify.moneyFormat || `{{amount}} ${currencyCode}`;
          return Shopify.formatMoney(amount, moneyFormat);
        } catch (error) {
          // Continue to fallback below
        }
      }

      const locale = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'en-US';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyCode
        }).format(amount / 100);
      } catch (error) {
        const symbol = currencySymbol ||
          (window.Shopify && Shopify.currency && Shopify.currency[`${currencyCode}_symbol`]) ||
          '';
        const fallbackAmount = (amount / 100).toFixed(2);
        return symbol ? `${symbol}${fallbackAmount}` : `${currencyCode} ${fallbackAmount}`;
      }
    }

    /**
     * Apply price and compare-at price to DOM elements
     * @param {HTMLElement|null} priceEl
     * @param {HTMLElement|null} compareEl
     * @param {number} priceCents
     * @param {number} compareCents
     * @param {string} currencyCode
     */
    function setPriceDisplay(priceEl, compareEl, priceCents, compareCents, currencyCode, currencySymbol = '') {
      if (priceEl) {
        priceEl.textContent = formatMoney(priceCents, currencyCode, currencySymbol);
      }

      if (compareEl) {
        if (compareCents > priceCents) {
          compareEl.textContent = formatMoney(compareCents, currencyCode, currencySymbol);
          compareEl.style.display = '';
          compareEl.setAttribute('aria-hidden', 'false');
        } else {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
          compareEl.setAttribute('aria-hidden', 'true');
        }
      }
    }

    /**
     * Update selling plans dropdown
     */
    const updateSellingPlans = (root, sellingPlanGroups) => {
      const sellingPlanSelect = root.querySelector(`#selling-plan-select-${sectionId}`);
      const hasPlans = hasActiveSellingPlans(sellingPlanGroups);

      if (!sellingPlanSelect) {
        return hasPlans;
      }

      // Clear options whenever no plans are available
      if (!hasPlans) {
        sellingPlanSelect.innerHTML = '';
        return false;
      }

      // Only update if dropdown is empty or we're in multi-product mode
      // This prevents duplicating options that were already rendered by Liquid
      const hasExistingOptions = sellingPlanSelect.options.length > 0;
      const isMultiProductMode = root.querySelector('.multi-product-selector') !== null;

      if (hasExistingOptions && !isMultiProductMode) {
        return true; // Keep Liquid-rendered options
      }

      // Clear existing options only when switching products
      sellingPlanSelect.innerHTML = '';

      const normalizedAppId = subscriptionAppId ? String(subscriptionAppId) : null;

      // Find selling plan group matching the subscription app id (if provided)
      const planGroup = Array.isArray(sellingPlanGroups)
        ? sellingPlanGroups.find(group => {
            if (!group) return false;
            if (normalizedAppId && group.app_id) {
              return String(group.app_id) === normalizedAppId;
            }
            return true;
          })
        : null;

      if (planGroup && Array.isArray(planGroup.selling_plans)) {
        planGroup.selling_plans.forEach(plan => {
          const option = document.createElement('option');
          option.value = plan.id;
          option.setAttribute('data-name', plan.name);

          // Format plan name
          let displayName = plan.name.replace('Delivery Every ', '');
          if (plan.name === 'Delivery Every 30 days') {
            displayName += ' - Most Popular';
            option.selected = true;
          }

          option.textContent = displayName;
          sellingPlanSelect.appendChild(option);
        });
      }

      return true;
    };

    /**
     * Update discount badge based on product data
     */
    const updateDiscountBadge = (root, productData) => {
      const discountBadge = root.querySelector(`#otp-discount-badge-${sectionId}`);
      if (!discountBadge) return;

      const isOnSale = productData.is_on_sale || false;
      const discountValue = productData.discount_value || 0;

      if (isOnSale && discountValue > 0) {
        discountBadge.textContent = `${discountValue}% OFF`;
        discountBadge.style.display = '';
      } else {
        discountBadge.style.display = 'none';
      }
    };

    /**
     * Update product title
     */
    const updateProductTitle = (root, title) => {
      // Update the main h1 title
      const h1Element = root.querySelector('.column--content .rte h1');
      const sanitizedTitle = sanitizeProductTitle(title);

      if (h1Element) {
        h1Element.textContent = sanitizedTitle;
      }

      // Also update any other title references
      const titleElements = root.querySelectorAll('[data-product-title]');
      titleElements.forEach(el => {
        el.textContent = sanitizedTitle;
      });
    };

    /**
     * Update product description
     */
    const updateProductDescription = (root, description) => {
      if (typeof description !== 'string') {
        return;
      }

      // Primary PDP description block
      const mainDescription = root.querySelector('.product-description');
      if (mainDescription) {
        mainDescription.innerHTML = description;
      }

      // Tabbed layout description (if present)
      const firstTabContent = root.querySelector('.tab-list-container .tab-content');
      if (firstTabContent) {
        const contentDiv = firstTabContent.querySelector('.tab-list-container__content-text');
        if (contentDiv) {
          contentDiv.innerHTML = description;
        }
      }

      // Accordion layout fallback
      const firstAccordionContent = root.querySelector('.accordion__content .tab-list-container__content-text');
      if (firstAccordionContent) {
        firstAccordionContent.innerHTML = description;
      }
    };

    /**
     * Animate images progressively as they load (instant visual feedback)
     * @param {HTMLElement} container - Container element with images
     * @param {Object} animationConfig - Animation settings {from, to, stagger}
     */
    const animateImagesProgressively = (container, animationConfig) => {
      if (typeof gsap === 'undefined') return;

      const images = container.querySelectorAll('img');

      // Kill any existing animations
      gsap.killTweensOf(images);

      images.forEach((img, index) => {
        // Set initial state immediately
        gsap.set(img, animationConfig.from);

        const animateImage = () => {
          gsap.to(img, {
            ...animationConfig.to,
            delay: index * (animationConfig.stagger || 0)
          });
        };

        // If image already loaded (from cache), animate immediately
        if (img.complete && img.naturalHeight !== 0) {
          animateImage();
        } else {
          // Animate as soon as this specific image loads
          const onLoad = () => {
            animateImage();
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onLoad);
          };
          img.addEventListener('load', onLoad);
          img.addEventListener('error', onLoad); // Show even if error
        }
      });
    };

    /**
     * Update product images in swiper galleries (optimized for instant switching)
     */
    const updateProductImages = (root, images) => {
      if (!images || images.length === 0) return;

      const normalizedImages = images
        .map(image => {
          const src = image && (image.src || image.url || '');
          const thumb = image && (image.thumb || image.thumbnail || src);
          const alt = image && image.alt ? image.alt : '';
          const width = image && image.width ? image.width : 1000;
          const height = image && image.height ? image.height : 1000;

          return { src, thumb, alt, width, height };
        })
        .filter(image => !!image.src);

      if (!normalizedImages.length) {
        return;
      }

      // Update main swiper
      const mainSwiper = root.querySelector('.product-image_main .swiper-wrapper');
      if (mainSwiper) {
        mainSwiper.innerHTML = normalizedImages.map((img, index) => {
          // Get base URL and version parameter if present
          const baseUrl = img.src.split('?')[0];
          const urlParams = new URLSearchParams(img.src.split('?')[1] || '');
          const vParam = urlParams.get('v') ? `&v=${urlParams.get('v')}` : '';

          return `
          <div class="swiper-slide">
            <div class="main">
              <img loading="eager"
                   decoding="${index === 0 ? 'sync' : 'async'}"
                   src="${baseUrl}?width=1000${vParam}"
                   srcset="${baseUrl}?width=600${vParam} 600w, ${baseUrl}?width=1000${vParam} 1000w, ${baseUrl}?width=1400${vParam} 1400w"
                   sizes="(max-width: 1024px) 100vw, 60vw"
                   width="${img.width}"
                   height="${img.height}"
                   alt="${img.alt}"
                   ${index === 0 ? 'fetchpriority="high"' : ''} />
            </div>
          </div>
          `;
        }).join('');

        // Animate images progressively as they load (instant feedback)
        animateImagesProgressively(mainSwiper, {
          from: { opacity: 0, scale: 0.98 },
          to: {
            duration: 0.35,
            opacity: 1,
            scale: 1,
            ease: 'power2.out',
            clearProps: 'all'
          },
          stagger: 0.03
        });
      }

      // Update thumbnail swiper
      const thumbnailSwiper = root.querySelector('.product-image_thumbnail .swiper-wrapper');
      if (thumbnailSwiper) {
        thumbnailSwiper.innerHTML = normalizedImages.map(img => {
          // Get base URL and version parameter if present
          const baseUrl = img.thumb.split('?')[0];
          const urlParams = new URLSearchParams(img.thumb.split('?')[1] || '');
          const vParam = urlParams.get('v') ? `&v=${urlParams.get('v')}` : '';

          return `
          <div class="swiper-slide">
            <div class="thumbnail">
              <img loading="eager"
                   decoding="async"
                   src="${baseUrl}?width=150${vParam}"
                   srcset="${baseUrl}?width=150${vParam} 150w, ${baseUrl}?width=300${vParam} 300w"
                   sizes="150px"
                   width="150"
                   height="150"
                   alt="${img.alt}" />
            </div>
          </div>
          `;
        }).join('');

        // Animate thumbnail images progressively as they load
        animateImagesProgressively(thumbnailSwiper, {
          from: { opacity: 0, y: 8 },
          to: {
            duration: 0.3,
            opacity: 1,
            y: 0,
            ease: 'power2.out',
            clearProps: 'all'
          },
          stagger: 0.02
        });
      }

      // Reinitialize swipers if they exist
      reinitializeSwipers(root);
    };

    /**
     * Reinitialize swiper instances after updating images
     */
    const reinitializeSwipers = (root) => {
      const mainSwiperEl = root.querySelector('.js-swiper-main');
      const thumbnailSwiperEl = root.querySelector('.js-swiper-thumbnail');
      const sectionWrapper = root.querySelector('.section--product');

      // Force loaded class to stay to prevent loading overlay flicker
      if (sectionWrapper) {
        sectionWrapper.classList.add('loaded');
      }

      // Destroy existing swiper instances
      if (mainSwiperEl && mainSwiperEl.swiper) {
        mainSwiperEl.swiper.destroy(true, true);
        delete mainSwiperEl.swiper;
        delete mainSwiperEl.dataset.carouselInitialized;
      }
      if (thumbnailSwiperEl && thumbnailSwiperEl.swiper) {
        thumbnailSwiperEl.swiper.destroy(true, true);
        delete thumbnailSwiperEl.swiper;
        delete thumbnailSwiperEl.dataset.carouselInitialized;
      }

      // Clear cache to force re-initialization
      cachedElements = {};

      // Small delay to ensure DOM is updated before reinitializing
      setTimeout(() => {
        if (sectionWrapper) {
          // Force loaded class again to ensure it stays
          sectionWrapper.classList.add('loaded');
          initMediaSwipers(root, sectionWrapper);
        }
      }, 100);
    };

    /**
     * Bind purchase type radio buttons to show/hide subscription options
     */
    const bindPurchaseTypeToggle = (root) => {
      const subscriptionRadio = root.querySelector('input[name="purchase_type_' + sectionId + '"][value="subscription"]');
      const onetimeRadio = root.querySelector('input[name="purchase_type_' + sectionId + '"][value="onetime"]');
      const subscriptionOptions = root.querySelector('#selling-plan-container-' + sectionId);

      if (!subscriptionRadio || !onetimeRadio || !subscriptionOptions) {
        return;
      }

      const toggleSubscriptionOptions = () => {
        if (subscriptionRadio.checked) {
          subscriptionOptions.style.display = 'block';
        } else {
          subscriptionOptions.style.display = 'none';
        }
      };

      // Initial state
      toggleSubscriptionOptions();

      // Listen for changes
      subscriptionRadio.addEventListener('change', toggleSubscriptionOptions);
      onetimeRadio.addEventListener('change', toggleSubscriptionOptions);
    };

    /**
     * Main initialization function
     */
    const initSection = () => {
      const root = getSectionRoot();
      if (!root) {
        return;
      }

      const wrapper = root.querySelector('.section--product');
      if (!wrapper) {
        return;
      }

      // Clear cache on re-init
      cachedElements = {};

      wrapper.classList.remove('loaded');

      initMediaSwipers(root, wrapper);
      bindVariantSwitch(root);
      bindMultiProductSelector(root);
      prefetchProductAssets(root);
      bindPurchaseTypeToggle(root);
      const form = root.querySelector('#add-to-cart-form-' + sectionId);
      if (form && form.hasAttribute('data-has-subscription')) {
        const initialHasSubscription = form.getAttribute('data-has-subscription') === 'true';
        toggleSubscriptionAvailability(root, initialHasSubscription);
      }
      bindForm(root);
      bindAccordions(root);
      initOfferProgressBar(root);

      wrapper.classList.add('loaded');

      // Announce to screen readers that section is ready
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'visually-hidden';
      announcement.textContent = 'Product section loaded';
      root.appendChild(announcement);
      setTimeout(() => announcement.remove(), 1000);
    };

    /**
     * Cleanup function for section unload
     */
    const cleanupSection = () => {
      const root = getSectionRoot();
      if (!root) return;

      // Remove all event listeners by marking as not bound
      delete root.dataset.accordionsBound;
      delete root.dataset.offerListenerBound;

      // Clear cached elements
      cachedElements = {};
      productDataCache.clear();
      prefetchedProducts.clear();
      prefetchedImages.clear();
    };

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initSection);
    } else {
      initSection();
    }

    // Shopify Theme Editor Events

    // Section load - when section is added or settings change
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail && event.detail.sectionId === sectionId) {
        initSection();
      }
    });

    // Section unload - cleanup when section is removed
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.detail && event.detail.sectionId === sectionId) {
        cleanupSection();
      }
    });

    // Section select - when section is selected in editor
    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail && event.detail.sectionId === sectionId) {
        const root = getSectionRoot();
        if (root) {
          // Scroll section into view smoothly
          root.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Add visual highlight for editor
          root.style.outline = '3px dashed var(--shake-primary-green)';
          root.style.outlineOffset = '5px';

          setTimeout(() => {
            root.style.outline = '';
            root.style.outlineOffset = '';
          }, 2000);
        }
      }
    });

    // Section deselect - cleanup visual indicators
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.detail && event.detail.sectionId === sectionId) {
        const root = getSectionRoot();
        if (root) {
          root.style.outline = '';
          root.style.outlineOffset = '';
        }
      }
    });

    // Block select - when a block within the section is selected
    document.addEventListener('shopify:block:select', (event) => {
      if (event.detail && event.detail.sectionId === sectionId) {
        const blockId = event.detail.blockId;
        const root = getSectionRoot();

        if (root && blockId) {
          // Find and open the selected tab/accordion
          const block = root.querySelector(`[data-id="block-${blockId}"]`);

          if (block && block.classList.contains('js-toggle')) {
            const isExpanded = block.getAttribute('aria-expanded') === 'true';

            if (!isExpanded) {
              block.click(); // Open the accordion
            }

            // Scroll to the block
            setTimeout(() => {
              block.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

              // Highlight the block
              const container = block.closest('.block_type--tab_item') || block.closest('.block_type--offer');
              if (container) {
                container.style.outline = '2px solid var(--shake-primary-orange)';
                container.style.outlineOffset = '3px';

                setTimeout(() => {
                  container.style.outline = '';
                  container.style.outlineOffset = '';
                }, 2000);
              }
            }, 300);
          }
        }
      }
    });

    // Block deselect - cleanup block highlights
    document.addEventListener('shopify:block:deselect', (event) => {
      if (event.detail && event.detail.sectionId === sectionId) {
        const root = getSectionRoot();
        if (root) {
          // Remove all block highlights
          root.querySelectorAll('[style*="outline"]').forEach(el => {
            el.style.outline = '';
            el.style.outlineOffset = '';
          });
        }
      }
    });
  };
})();
